import * as HttpStatus from 'http-status-codes';
import fetch from 'isomorphic-fetch';
import jwtDecode from 'jwt-decode';

import { clientConfig } from 'config/client';
import localStorage from 'core/local-storage/localStorage';

import { MayBe } from '../types';
import { IRequestHeaders, IRequestOptions } from './types';

export enum STATUSES {
  INITIAL = 'INITIAL',
  REQUEST = 'REQUEST',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export class Api {
  public static readonly updateToken = async () => {
    try {
      if (!Api.refreshing) {
        Api.refreshing = Api.fetchNewToken();
      }
      const token: string | null = await Api.refreshing;

      if (token) {
        localStorage.set(clientConfig.tokenParamName, token);
      } else {
        Api.logout();
      }
    } catch (e) {
      Api.logout();
    } finally {
      Api.refreshing = null;
    }
  };

  private static refreshing: any = null;

  private static async fetchNewToken(): Promise<string | null> {
    const refreshUrl = `${clientConfig.eagleApiUrl}/eagle-auth/refresh`;
    const token: MayBe<string> = localStorage.get(clientConfig.tokenParamName);

    if (!token) {
      return null;
    }

    const parsedToken: any = jwtDecode(token);

    const options = {
      body: JSON.stringify({ code: parsedToken && parsedToken.refresh_token }),
      headers: {
        ...{
          'Content-Type': 'application/json',
          'X-App': 'people-directory',
        },
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
    };

    const response = await fetch(refreshUrl, options);

    if (response.status >= HttpStatus.BAD_REQUEST) {
      return null;
    }

    const json = await response.json();

    return json.token;
  }

  private static logout(): void {
    localStorage.remove(clientConfig.tokenParamName);
    localStorage.remove('X-Intranet-Token');
    window.location.href = '/logout';
  }

  private readonly doNotRefreshToken: boolean;
  private readonly initialData: any;
  private readonly namespace: string;
  private readonly options: any;
  private readonly throwable: boolean;
  private readonly url: string;
  private readonly useEagleApi: boolean;

  public constructor({
    url,
    useEagleApi,
    options = {},
    namespace,
    initialData,
    doNotRefreshToken,
    throwable = false,
  }: any) {
    this.useEagleApi = useEagleApi;
    this.url = `${
      useEagleApi ? clientConfig.eagleApiUrl : clientConfig.apiPath
    }${url ? url : ''}`;
    this.options = options;
    this.initialData = initialData;
    this.namespace = namespace;
    this.throwable = throwable;
    this.doNotRefreshToken = doNotRefreshToken;
  }

  public simpleFetch = async (
    { url, options = {} } = {} as any,
  ): Promise<any> => {
    try {
      const token: MayBe<string> = localStorage.get(
        clientConfig.tokenParamName,
      );
      const parentUrl: string = this.useEagleApi
        ? clientConfig.eagleApiUrl
        : clientConfig.apiPath;
      const res = await fetch(url ? `${parentUrl}${url}` : this.url, {
        ...this.options,
        ...options,
        headers: this.getHeaders(options),
      });

      if (res.status === HttpStatus.UNAUTHORIZED && !this.doNotRefreshToken) {
        if (!token) {
          throw new Error(String(HttpStatus.UNAUTHORIZED));
        }
        await Api.updateToken();

        return this.simpleFetch({ url, options });
      }

      const xIntranetToken = res.headers.get('X-Intranet-Token');

      if (
        xIntranetToken &&
        xIntranetToken !== '=' &&
        xIntranetToken !== localStorage.get('X-Intranet-Token')
      ) {
        localStorage.set('X-Intranet-Token', xIntranetToken);
      }

      if (!res.ok) {
        let error;

        try {
          const response = await res.json();
          error = response.message;
        } catch (e) {
          throw new Error(String(res.status));
        }
        throw new Error(error || String(res.status));
      }

      const { success, ...data } = await res.json();
      return data;
    } catch (err) {
      const { message } = err as Error;
      throw new Error(message);
    }
  };

  public fetch = ({ url, options = {}, urlParams = '' } = {} as any) => async (
    dispatch: any,
  ) => {
    dispatch({
      type: `${this.namespace}:${STATUSES.REQUEST}`,
    });

    try {
      const token: MayBe<string> = localStorage.get(
        clientConfig.tokenParamName,
      );
      const parentUrl: string = this.useEagleApi
        ? clientConfig.eagleApiUrl
        : clientConfig.apiPath;
      const res = await fetch(
        url ? `${parentUrl}${url}` : this.url + urlParams,
        {
          ...this.options,
          ...options,
          headers: this.getHeaders(options),
        },
      );

      if (res.status === HttpStatus.UNAUTHORIZED && !this.doNotRefreshToken) {
        if (!token) {
          throw new Error(String(HttpStatus.UNAUTHORIZED));
        }
        await Api.updateToken();

        return dispatch(this.fetch({ url, options }));
      }

      const xIntranetToken = res.headers.get('X-Intranet-Token');

      if (
        xIntranetToken &&
        xIntranetToken !== '=' &&
        xIntranetToken !== localStorage.get('X-Intranet-Token')
      ) {
        localStorage.set('X-Intranet-Token', xIntranetToken);
      }

      if (!res.ok) {
        let error;

        try {
          const response = await res.json();
          error = response.message;
        } catch (e) {
          throw new Error(String(res.status));
        }
        throw new Error(error || String(res.status));
      }

      const { success, ...data } = await res.json();

      dispatch({
        payload: data,
        type: `${this.namespace}:${STATUSES.SUCCESS}`,
      });
      return data;
    } catch (error) {
      const { message } = error as Error;

      dispatch({
        payload: message,
        type: `${this.namespace}:${STATUSES.FAILED}`,
      });
      if (this.throwable) {
        throw new Error(message);
      }
    }
  };

  public reducer = (
    state = {
      data: this.initialData,
      error: null,
      status: STATUSES.INITIAL,
    },
    action: any,
  ) => {
    switch (action.type) {
      case `${this.namespace}:${STATUSES.REQUEST}`:
        return {
          ...state,
          error: null,
          status: STATUSES.REQUEST,
        };
      case `${this.namespace}:${STATUSES.SUCCESS}`:
        return {
          ...state,
          data: action.payload,
          status: STATUSES.SUCCESS,
        };
      case `${this.namespace}:${STATUSES.FAILED}`:
        return {
          ...state,
          error: action.payload,
          status: STATUSES.FAILED,
        };
      case `${this.namespace}:${STATUSES.INITIAL}`:
        return {
          data: this.initialData,
          error: null,
          status: STATUSES.INITIAL,
        };
      default:
        return state;
    }
  };

  public reset = () => (dispatch: any) => {
    dispatch({
      type: `${this.namespace}:${STATUSES.INITIAL}`,
    });
  };

  private readonly getHeaders = (options: IRequestOptions) => {
    const token: MayBe<string> = localStorage.get(
      this.useEagleApi
        ? clientConfig.eagleTokenParamName
        : clientConfig.tokenParamName,
    );
    const xIntranetToken: MayBe<string> = localStorage.get('X-Intranet-Token');

    const headers: IRequestHeaders = {
      'Content-Type': 'application/json',
    };

    if (Boolean(token)) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (typeof xIntranetToken === 'string') {
      headers['X-Intranet-Token'] = xIntranetToken;
    }

    return {
      ...headers,
      ...(this.options.headers || {}),
      ...(options.headers || {}),
    };
  };
}
